import React, { useCallback, useMemo, useState } from 'react';
import CSSModules from 'react-css-modules';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import isBoolean from 'lodash/isBoolean';
import debounce from 'lodash/debounce';
import size from 'lodash/size';

import Popup from '../../Popup/index';
import Button from '../../../Button/index';
import ChipInput from '../../Selectbox/ChipInput';
import SelectableListWithTabs from '../SelectableListWithTabs';
import { SelectedSizeInfo } from './SelectedSizeInfo';
import { SEARCH_TABS } from '../../../../constants/props';
import { NEW_CHAT } from '../../../../constants/strings';
import styles from './index.styl';

const SearchDialogImpl = ({
  isOpen,
  title,
  buttonTitle,
  searchPlaceholder,
  defaultState,
  onClose,
  onCreate,
  type,
  activeChannelId,
  contextUserId,
  extraValidation,
  defaultIcon,
  children,
  users,
  loading,
  succeed,
  getUsers,
  clear,
  withTabs,
  emptyState,
}) => {
  const [activeTab, setActiveTab] = useState(withTabs ? withTabs[0] : null);
  const [query, setQuery, resetSearch] = useSearchQuery(clear);
  const [
    selected,
    deleteSelected,
    addSelected,
    resetSelected,
  ] = useSelectedList(resetSearch);

  const debouncedFetch = useCallback(
    debounce(search => {
      const ids = selected.map(({ id }) => id);

      if (!search) {
        return clear();
      }
      getUsers({
        channelId: activeChannelId,
        userId: contextUserId,
        query: search,
        limit: 20,
        teamSearch: activeTab === SEARCH_TABS.TEAMS,
        ids,
        type,
      });
    }, 200),
    [selected, activeTab],
  );

  const handleClose = useCallback(() => {
    onClose();
    resetSearch();
    resetSelected();
    if (withTabs) setActiveTab(withTabs[0]);
  }, []);

  const handleChangeInput = value => {
    setQuery(value);
    debouncedFetch(value);
  };

  const handleTabSelect = value => {
    setActiveTab(value);
    resetSearch();
  };

  const handleSubmit = useCallback(() => {
    const ids = selected.map(({ id }) => id);

    onCreate(ids);
    handleClose();
  }, [selected, onCreate]);

  const isFormDisabled = useMemo(() => {
    const isExtraInvalid = isBoolean(extraValidation)
      ? !extraValidation
      : false;

    return size(selected) < 1 || isExtraInvalid;
  }, [selected, extraValidation]);

  return (
    <Popup
      opened={isOpen}
      onClose={handleClose}
      title={title}
      width="medium"
      strictClose
    >
      <div styleName="content">
        {children}
        <div styleName="content-input">
          <div styleName="input-wrapper">
            <ChipInput
              placeholder={searchPlaceholder}
              onChange={handleChangeInput}
              onDelete={deleteSelected}
              selected={selected}
              value={query}
              autoFocus
            />
          </div>
          <Button
            styleName="input-button"
            disabled={isFormDisabled}
            onClick={handleSubmit}
            color="primary"
            type="button"
          >
            {buttonTitle}
          </Button>
        </div>
        <div styleName="list-wrapper">
          <SelectedSizeInfo
            size={size(selected)}
            textId={NEW_CHAT.youAddedMembers}
          />
          <SelectableListWithTabs
            withTabs={withTabs}
            users={users}
            onTabSelect={handleTabSelect}
            onSelect={addSelected}
            loading={loading}
            succeed={succeed}
            defaultIcon={defaultIcon}
            defaultState={defaultState}
            emptyState={emptyState}
          />
        </div>
      </div>
    </Popup>
  );
};

export const SearchDialogPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  buttonTitle: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  defaultState: PropTypes.string.isRequired,
  emptyState: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  type: PropTypes.string,
  activeChannelId: PropTypes.number,
  contextUserId: PropTypes.number,
  withTabs: PropTypes.array,
};

SearchDialogImpl.propTypes = {
  ...SearchDialogPropTypes,
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  succeed: PropTypes.bool.isRequired,
  getUsers: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
};

export default compose(CSSModules(styles, { allowMultiple: true }))(
  SearchDialogImpl,
);

function useSelectedList(clear) {
  const [selected, setSelected] = useState([]);

  const deleteSelected = useCallback(
    item => {
      setSelected(selected.filter(({ id }) => item.id !== id));
    },
    [selected],
  );

  const addSelected = useCallback(
    item => {
      setSelected([...selected, item]);
      clear();
    },
    [selected],
  );

  const resetSelected = useCallback(() => {
    setSelected([]);
  }, []);

  return [selected, deleteSelected, addSelected, resetSelected];
}

function useSearchQuery(clear) {
  const [query, setQuery] = useState('');

  const handleClear = useCallback(() => {
    setQuery('');
    clear();
  }, []);

  return [query, setQuery, handleClear];
}



// WEBPACK FOOTER //
// ./src/components/common/SearchDialog/SearchDialogImpl/index.jsx